import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { faStamp } from '@fortawesome/free-solid-svg-icons';
import {
  authorizationCaseService,
  CURRENT_OWNER_LABELS,
  WAITING_FOR_LABELS,
  type AuthorizationCurrentOwner,
  type AuthorizationWaitingFor,
} from '@/services/authorization-case.service';
import { SkeletonPage } from '@/components/skeletons';
import { RouteStatusCell } from './components/RouteStatusCell';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { Pagination } from '@/components/shared/Pagination';
import { ROUTES } from '@/routes/routes';

const PAGE_SIZE = 20;

const SELECT_CLASS =
  'flex h-10 rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50';

const OWNER_BADGE: Record<AuthorizationCurrentOwner, string> = {
  ENGINEER: 'bg-slate-500/10 text-slate-600',
  OPS: 'bg-blue-500/10 text-blue-700',
  COMMERCIAL: 'bg-purple-500/10 text-purple-700',
  ADMIN: 'bg-amber-500/10 text-amber-700',
};

const WAITING_BADGE: Record<AuthorizationWaitingFor, string> = {
  ENGINEER: 'bg-slate-500/10 text-slate-600',
  OPS: 'bg-blue-500/10 text-blue-700',
  COMMERCIAL: 'bg-purple-500/10 text-purple-700',
  ADMIN: 'bg-amber-500/10 text-amber-700',
  CUSTOMER: 'bg-orange-500/10 text-orange-700',
};

function elapsedSince(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return '<1 Hour';
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  if (days === 0) return `${hours} Hour${hours === 1 ? '' : 's'}`;
  return `${days} Day${days === 1 ? '' : 's'}${remHours ? ` ${remHours} Hour${remHours === 1 ? '' : 's'}` : ''}`;
}

/** Authorization Case Management (docs/AUTHORIZATION_CASE_MANAGEMENT_DESIGN.md §13) —
 * the tenant-wide queue of all authorization cases regardless of source type. */
export function AuthorizationCenterPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [ownerFilter, setOwnerFilter] = useState<AuthorizationCurrentOwner | ''>('');
  const [waitingFilter, setWaitingFilter] = useState<AuthorizationWaitingFor | ''>('');
  const [statusFilter, setStatusFilter] = useState<'PENDING' | 'APPROVED' | 'REJECTED' | ''>('');

  // Stage 5F — the multi-case AGGREGATE, not the plain list. Design §12.2
  // computes route status, deviation/override counts and gate status
  // server-side inside this endpoint precisely so the client never re-derives
  // them. Rule O: ONE request per page, a fixed six queries behind it.
  const { data, isLoading } = useQuery({
    queryKey: ['authorization-cases-journey', page, ownerFilter, waitingFilter, statusFilter],
    queryFn: () =>
      authorizationCaseService.listJourney({
        page,
        pageSize: PAGE_SIZE,
        currentOwner: ownerFilter || undefined,
        waitingFor: waitingFilter || undefined,
        status: statusFilter || undefined,
      }),
  });

  if (isLoading) return <SkeletonPage />;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Authorization Center"
        description="Every authorization case, one queue, regardless of where it originated"
      />

      <div className="flex flex-wrap gap-3">
        <select
          value={ownerFilter}
          onChange={(e) => {
            setOwnerFilter(e.target.value as AuthorizationCurrentOwner | '');
            setPage(1);
          }}
          className={SELECT_CLASS}
          aria-label="Filter by current owner"
        >
          <option value="">All Owners</option>
          {(Object.keys(CURRENT_OWNER_LABELS) as AuthorizationCurrentOwner[]).map((o) => (
            <option key={o} value={o}>
              {CURRENT_OWNER_LABELS[o]}
            </option>
          ))}
        </select>

        <select
          value={waitingFilter}
          onChange={(e) => {
            setWaitingFilter(e.target.value as AuthorizationWaitingFor | '');
            setPage(1);
          }}
          className={SELECT_CLASS}
          aria-label="Filter by waiting for"
        >
          <option value="">Waiting For — Any</option>
          {(Object.keys(WAITING_FOR_LABELS) as AuthorizationWaitingFor[]).map((w) => (
            <option key={w} value={w}>
              {WAITING_FOR_LABELS[w]}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as typeof statusFilter);
            setPage(1);
          }}
          className={SELECT_CLASS}
          aria-label="Filter by status"
        >
          <option value="">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
      </div>

      {!data || data.data.length === 0 ? (
        <EmptyState
          icon={faStamp}
          title="No authorization cases"
          description="Cases created for findings or procedure steps requiring authorization will appear here."
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Case #</th>
                <th className="px-4 py-3 font-medium">Inspection</th>
                {/* Equipment identity sits immediately beside the inspection it
                    belongs to — the two read as one "what is this about?" pair.
                    One column, not two, so the row stays as dense as before. */}
                <th className="px-4 py-3 font-medium">Equipment</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Priority</th>
                <th className="px-4 py-3 font-medium">Current Owner</th>
                <th className="px-4 py-3 font-medium">Waiting For</th>
                {/* Stage 5F — the expected-route layer, clearly its own column
                    and never interleaved with the actual owner columns. */}
                <th className="px-4 py-3 font-medium">Expected Route</th>
                <th className="px-4 py-3 font-medium">Elapsed</th>
                <th className="px-4 py-3 font-medium">Requested By</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((c) => (
                <tr
                  key={c.id}
                  className="cursor-pointer border-b border-border last:border-0 hover:bg-muted/30"
                  onClick={() => navigate(ROUTES.AUTHORIZATION_CASE_DETAIL.replace(':id', c.id))}
                >
                  <td className="px-4 py-3 font-medium text-foreground">{c.caseNumber}</td>
                  <td className="px-4 py-3 text-foreground">{c.inspectionNumber}</td>
                  {/* ONE compact identity block in ONE cell: Asset # directly
                      above Serial #, never side by side and never a second
                      table row. `leading-tight` keeps the two lines inside the
                      height the existing single-line cells already occupy, so
                      the row does not grow. Asset # carries the stronger weight;
                      Serial # is deliberately secondary. */}
                  <td className="whitespace-nowrap px-4 py-3 leading-tight">
                    <div className="font-medium text-foreground">
                      <span className="text-muted-foreground">Asset #:</span>{' '}
                      {c.equipmentAssetNumber || '—'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Serial #: {c.equipmentSerialNumber || '—'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {c.approvalRequestTypeName ?? c.customApprovalName ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    {c.priorityName ? (
                      <span
                        className="rounded-full px-2 py-0.5 text-xs font-medium"
                        style={{
                          backgroundColor: `${c.priorityColor}1A`,
                          color: c.priorityColor ?? undefined,
                        }}
                      >
                        {c.priorityName}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${OWNER_BADGE[c.currentOwner]}`}
                    >
                      {CURRENT_OWNER_LABELS[c.currentOwner]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${WAITING_BADGE[c.waitingFor]}`}
                    >
                      {WAITING_FOR_LABELS[c.waitingFor]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <RouteStatusCell route={c.route} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {elapsedSince(c.waitingSinceAt)}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{c.requestedByName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && (
        <Pagination page={page} pageSize={PAGE_SIZE} total={data.total} onPageChange={setPage} />
      )}
    </div>
  );
}
